const FBT_STOREFRONT_QUOTE_MUTATION = `
mutation FbtQuote(
  $input: CartInput!
  $country: CountryCode
  $language: LanguageCode
) @inContext(country: $country, language: $language) {
  cartCreate(input: $input) {
    cart {
      id
      cost {
        subtotalAmount {
          amount
          currencyCode
        }
        totalAmount {
          amount
          currencyCode
        }
      }
      discountAllocations {
        discountedAmount {
          amount
          currencyCode
        }
        ... on CartAutomaticDiscountAllocation {
          title
        }
        ... on CartCodeDiscountAllocation {
          code
        }
        ... on CartCustomDiscountAllocation {
          title
        }
      }
      lines(first: 50) {
        nodes {
          merchandise {
            ... on ProductVariant {
              id
            }
          }
          quantity
          cost {
            subtotalAmount {
              amount
              currencyCode
            }
            totalAmount {
              amount
              currencyCode
            }
          }
          discountAllocations {
            discountedAmount {
              amount
              currencyCode
            }
            ... on CartAutomaticDiscountAllocation {
              title
            }
            ... on CartCodeDiscountAllocation {
              code
            }
            ... on CartCustomDiscountAllocation {
              title
            }
          }
        }
      }
    }
    userErrors {
      field
      message
    }
    warnings {
      code
      message
      target
    }
  }
}
`;

const FBT_STOREFRONT_API_ENDPOINT = '/api/2025-04/graphql.json';
const FBT_STOREFRONT_ACCESS_TOKEN = '03d86b8dc626bc6b1c499e4441d8c6d4';

if (!customElements.get('frequently-bought-together')) {
  customElements.define(
    'frequently-bought-together',
    class FrequentlyBoughtTogether extends HTMLElement {
      connectedCallback() {
        this.mainProductId = this.dataset.mainProductId;
        this.mainVariantId = this.toInt(this.dataset.mainVariantId);
        this.mainVariantPrice = this.toInt(this.dataset.mainPrice);
        this.mainVariantAvailable = true;
        this.mainQuantity = Math.max(this.toInt(this.dataset.mainQuantity, 1), 1);
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.storefrontApiEndpoint = FBT_STOREFRONT_API_ENDPOINT;
        this.storefrontCountry = (this.dataset.storefrontCountry || '').trim().toUpperCase();
        this.storefrontLanguage = (this.dataset.storefrontLanguage || '').trim().toUpperCase();
        this.storefrontAccessToken = FBT_STOREFRONT_ACCESS_TOKEN;

        this.mainVariants = this.parseMainVariants();
        this.mainVariantTitleEl = this.querySelector('[data-main-variant-title]');

        this.mainItem = this.querySelector('[data-main-item]');
        this.addonItems = Array.from(
          this.querySelectorAll('[data-fbt-item]:not([data-main-item])')
        );

        this.totalDiscountedEl = this.querySelector('[data-total-discounted]');
        this.totalOriginalEl = this.querySelector('[data-total-original]');
        this.totalSavingEl = this.querySelector('[data-total-saving]');
        this.addButton = this.querySelector('[data-add-selected]');

        this.quoteState = 'idle';
        this.quotedSubtotal = 0;
        this.quotedTotal = 0;
        this.quotedSavings = 0;
        this.quoteDebounceTimer = null;
        this.quoteRequestId = 0;
        this.quoteAbortController = null;

        this.onDocumentChange = this.handleDocumentChange.bind(this);
        this.onProductInfoLoaded = this.handleProductInfoLoaded.bind(this);
        this.onAddClick = this.handleAddToCart.bind(this);

        document.addEventListener('change', this.onDocumentChange);
        document.addEventListener('product-info:loaded', this.onProductInfoLoaded);
        this.addButton?.addEventListener('click', this.onAddClick);

        if (this.mainItem) {
          const mainSelector = this.mainItem.querySelector('[data-variant-select]');
          this.initializeAddonOptionPicker(this.mainItem);

          if (mainSelector && mainSelector.tagName === 'SELECT') {
            mainSelector.addEventListener('change', () => {
              this.syncAddonOptionUiFromSelector(this.mainItem);
              this.updateMainVariantFromFbtSelector();
            });
          }

          this.syncAddonOptionUiFromSelector(this.mainItem);
        }

        this.addonItems.forEach((item) => {
          const selector = item.querySelector('[data-variant-select]');
          this.initializeAddonOptionPicker(item);

          if (selector && selector.tagName === 'SELECT') {
            selector.addEventListener('change', () => {
              this.syncAddonOptionUiFromSelector(item);
              this.updateAddonItemPrice(item);
              this.updateTotals();
            });
          }

          this.syncAddonOptionUiFromSelector(item);
          this.updateAddonItemPrice(item);
        });

        this.updateMainVariantFromProductForm();
        this.updateTotals();
      }

      disconnectedCallback() {
        if (this.quoteDebounceTimer) {
          window.clearTimeout(this.quoteDebounceTimer);
          this.quoteDebounceTimer = null;
        }

        if (this.quoteAbortController) {
          this.quoteAbortController.abort();
          this.quoteAbortController = null;
        }

        this.quoteRequestId += 1;

        document.removeEventListener('change', this.onDocumentChange);
        document.removeEventListener('product-info:loaded', this.onProductInfoLoaded);
        this.addButton?.removeEventListener('click', this.onAddClick);
      }

      handleDocumentChange(event) {
        const target = event.target;

        if (target?.matches(this.mainVariantInputSelector())) {
          this.updateMainVariantFromProductForm(target);
        }
      }

      handleProductInfoLoaded() {
        this.updateMainVariantFromProductForm();
      }

      parseMainVariants() {
        const script = this.querySelector('[data-main-variants]');
        if (!script) return new Map();

        try {
          const variants = JSON.parse(script.textContent);
          return new Map(variants.map((variant) => [String(variant.id), variant]));
        } catch (error) {
          return new Map();
        }
      }

      mainVariantInputSelector() {
        return `product-info[data-product-id="${this.mainProductId}"] form[action*="/cart/add"] input[name="id"]`;
      }

      findMainVariantInput() {
        return document.querySelector(this.mainVariantInputSelector());
      }

      updateMainVariantFromProductForm(mainInput) {
        const variantInput = mainInput || this.findMainVariantInput();
        if (!variantInput) {
          this.syncMainVariantSelector();
          this.updateMainPriceDisplay();
          return;
        }

        this.mainVariantId = this.toInt(variantInput.value, this.mainVariantId);
        const mainVariant = this.mainVariants.get(String(this.mainVariantId));

        if (mainVariant) {
          this.mainVariantPrice = this.toInt(mainVariant.price, this.mainVariantPrice);
          this.mainVariantAvailable = Boolean(mainVariant.available);
          if (this.mainVariantTitleEl) {
            this.mainVariantTitleEl.textContent = mainVariant.title === 'Default Title' ? '' : mainVariant.title;
          }
        } else {
          this.mainVariantAvailable = true;
        }

        this.syncMainVariantSelector();
        this.updateMainPriceDisplay();
        this.updateTotals();
      }

      updateMainVariantFromFbtSelector() {
        if (!this.mainItem) return;

        const selection = this.getAddonSelection(this.mainItem);
        if (!selection || !selection.id) return;

        this.mainVariantId = selection.id;
        this.mainVariantPrice = selection.price;
        this.mainVariantAvailable = selection.available;

        const mainVariant = this.mainVariants.get(String(this.mainVariantId));
        if (this.mainVariantTitleEl) {
          this.mainVariantTitleEl.textContent =
            mainVariant && mainVariant.title !== 'Default Title' ? mainVariant.title : '';
        }

        this.syncMainVariantToProductForm();
        this.updateMainPriceDisplay();
        this.updateTotals();
      }

      syncMainVariantSelector() {
        if (!this.mainItem || !this.mainVariantId) return;

        const selector = this.mainItem.querySelector('[data-variant-select]');
        if (!selector || selector.tagName !== 'SELECT') return;

        if (selector.value !== String(this.mainVariantId)) {
          selector.value = String(this.mainVariantId);
        }

        this.syncAddonOptionUiFromSelector(this.mainItem);
      }

      syncMainVariantToProductForm() {
        const variantInput = this.findMainVariantInput();
        if (!variantInput || !this.mainVariantId) return;

        const nextValue = String(this.mainVariantId);
        if (variantInput.value === nextValue) return;

        variantInput.value = nextValue;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateMainPriceDisplay() {
        const mainItemEl = this.querySelector('[data-main-item]');
        if (!mainItemEl) return;

        const mainPriceEl = mainItemEl.querySelector('[data-price-current]');
        const compareEl = mainItemEl.querySelector('[data-price-compare]');
        const mainVariant = this.mainVariants.get(String(this.mainVariantId));
        const comparePrice = Math.max(
          this.toInt(mainVariant?.compare_at_price, this.mainVariantPrice),
          this.mainVariantPrice
        );

        if (mainPriceEl) {
          mainPriceEl.textContent = this.formatMoney(this.mainVariantPrice);
        }

        if (compareEl) {
          if (comparePrice > this.mainVariantPrice) {
            compareEl.textContent = this.formatMoney(comparePrice);
            compareEl.classList.remove('is-hidden');
          } else {
            compareEl.textContent = '';
            compareEl.classList.add('is-hidden');
          }
        }

        this.updateItemDiscountBadge(mainItemEl, this.mainVariantPrice, comparePrice);
      }

      toggleItemState(item) {
        item.classList.remove('is-unchecked');
      }

      normalizeVariantData(variant) {
        const options = Array.isArray(variant?.options)
          ? variant.options.map((optionValue) => String(optionValue || ''))
          : [
              String(variant?.option1 || ''),
              String(variant?.option2 || ''),
              String(variant?.option3 || ''),
            ];

        return {
          id: this.toInt(variant?.id),
          available: Boolean(variant?.available),
          price: this.toInt(variant?.price),
          compareAt: this.toInt(variant?.compare_at_price, this.toInt(variant?.price)),
          options,
          title: this.normalizeVariantTitle(variant?.title, options),
        };
      }

      normalizeVariantTitle(rawTitle, options = []) {
        const normalizedTitle = String(rawTitle || '').trim();
        if (normalizedTitle && normalizedTitle !== 'Default Title') {
          return normalizedTitle;
        }

        if (!Array.isArray(options)) return '';

        return options
          .map((optionValue) => String(optionValue || '').trim())
          .filter((optionValue) => optionValue && optionValue !== 'Default Title')
          .join(' / ');
      }

      getVariantOptionsFromSelectOption(option) {
        if (!option) return ['', '', ''];

        return [
          option?.dataset?.option1 || '',
          option?.dataset?.option2 || '',
          option?.dataset?.option3 || '',
        ];
      }

      parseAddonVariants(item) {
        const variantsScript = item.querySelector('[data-addon-variants]');

        if (!variantsScript) {
          if (item?.hasAttribute('data-main-item')) {
            return Array.from(this.mainVariants.values()).map((variant) =>
              this.normalizeVariantData(variant)
            );
          }
          return [];
        }

        try {
          const variants = JSON.parse(variantsScript.textContent);
          if (!Array.isArray(variants)) return [];

          return variants.map((variant) => this.normalizeVariantData(variant));
        } catch (error) {
          return [];
        }
      }

      initializeAddonOptionPicker(item) {
        const optionButtons = item.querySelectorAll('[data-option-button]');
        if (!optionButtons.length) return;

        item.fbtAddonVariants = this.parseAddonVariants(item);
        item.fbtSelectedOptions = {};

        optionButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.handleAddonOptionButtonClick(item, button);
          });
        });
      }

      syncAddonOptionUiFromSelector(item) {
        const selector = item.querySelector('[data-variant-select]');
        if (!selector || selector.tagName !== 'SELECT') return;

        const variants = item.fbtAddonVariants || [];
        const selectedVariantId = this.toInt(selector.value);
        let selectedVariant = variants.find((variant) => variant.id === selectedVariantId);

        if (!selectedVariant) {
          const selectedOption = selector.options[selector.selectedIndex];
          const selectedOptions = this.getVariantOptionsFromSelectOption(selectedOption);
          selectedVariant = {
            id: selectedVariantId,
            available: selectedOption?.dataset?.available === 'true',
            options: selectedOptions,
            title: this.normalizeVariantTitle('', selectedOptions),
          };
        }

        if (!selectedVariant) return;

        item.fbtSelectedOptions = {
          1: selectedVariant.options?.[0] || '',
          2: selectedVariant.options?.[1] || '',
          3: selectedVariant.options?.[2] || '',
        };

        this.syncAddonOptionButtons(item);
        this.syncAddonAvailability(item, selectedVariant.available);
      }

      syncAddonOptionButtons(item) {
        const selectedOptions = item.fbtSelectedOptions || {};
        const groups = item.querySelectorAll('[data-option-group]');
        if (!groups.length) return;

        groups.forEach((group) => {
          const optionPosition = this.toInt(group.dataset.optionPosition);
          const selectedValue = selectedOptions[optionPosition] || '';
          const selectedLabel = group.querySelector('[data-selected-option-value]');
          if (selectedLabel) selectedLabel.textContent = selectedValue;

          group.querySelectorAll('[data-option-button]').forEach((button) => {
            const optionValue = button.dataset.optionValue || '';
            const isSelected = optionValue === selectedValue;
            const isAvailable = this.isAddonOptionValueAvailable(item, optionPosition, optionValue);

            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            button.disabled = !isAvailable;
          });
        });
      }

      isAddonOptionValueAvailable(item, optionPosition, optionValue) {
        const variants = item.fbtAddonVariants || [];
        if (!variants.length) return true;

        const selectedOptions = item.fbtSelectedOptions || {};

        return variants.some((variant) => {
          if (!variant.available) return false;
          if ((variant.options?.[optionPosition - 1] || '') !== optionValue) return false;

          return Object.entries(selectedOptions).every(([position, selectedValue]) => {
            const parsedPosition = this.toInt(position);
            if (!parsedPosition || parsedPosition === optionPosition || !selectedValue) return true;
            return (variant.options?.[parsedPosition - 1] || '') === selectedValue;
          });
        });
      }

      handleAddonOptionButtonClick(item, button) {
        if (button.disabled) return;

        const optionPosition = this.toInt(button.dataset.optionPosition);
        const optionValue = button.dataset.optionValue || '';
        if (!optionPosition || !optionValue) return;

        const nextSelection = {
          ...(item.fbtSelectedOptions || {}),
          [optionPosition]: optionValue,
        };

        const nextVariant = this.resolveAddonVariantFromOptions(item, nextSelection);
        if (!nextVariant) return;

        this.applyAddonVariant(item, nextVariant, true);
      }

      resolveAddonVariantFromOptions(item, selection) {
        const variants = item.fbtAddonVariants || [];
        if (!variants.length) return null;

        const matchesSelection = (variant) =>
          Object.entries(selection).every(([position, selectedValue]) => {
            const parsedPosition = this.toInt(position);
            if (!parsedPosition || !selectedValue) return true;
            return (variant.options?.[parsedPosition - 1] || '') === selectedValue;
          });

        return (
          variants.find((variant) => variant.available && matchesSelection(variant)) ||
          variants.find((variant) => variant.available) ||
          variants[0] ||
          null
        );
      }

      applyAddonVariant(item, variant, shouldDispatchChange = false) {
        const selector = item.querySelector('[data-variant-select]');
        if (!selector || selector.tagName !== 'SELECT') return;

        selector.value = String(variant.id);

        item.fbtSelectedOptions = {
          1: variant.options?.[0] || '',
          2: variant.options?.[1] || '',
          3: variant.options?.[2] || '',
        };

        this.syncAddonOptionButtons(item);
        this.syncAddonAvailability(item, variant.available);

        if (shouldDispatchChange) {
          selector.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      syncAddonAvailability(item, isAvailable) {
        if (item?.hasAttribute('data-main-item')) return;

        const checkbox = item.querySelector('[data-item-enabled]');
        if (checkbox) {
          checkbox.disabled = !isAvailable;
          if (!isAvailable) checkbox.checked = false;
          this.toggleItemState(item);
          return;
        }

        item.classList.toggle('is-unavailable', !isAvailable);
      }

      getAddonSelection(item) {
        const selector = item.querySelector('[data-variant-select]');
        if (!selector) return null;

        if (selector.tagName === 'SELECT') {
          const option = selector.options[selector.selectedIndex];
          if (!option) return null;
          const selectedVariantId = this.toInt(option.value);
          const options = this.getVariantOptionsFromSelectOption(option);
          const variants = item.fbtAddonVariants || [];
          const selectedVariant = variants.find((variant) => variant.id === selectedVariantId);

          return {
            id: selectedVariantId,
            price: this.toInt(option.dataset.price),
            compareAt: this.toInt(option.dataset.compareAt, this.toInt(option.dataset.price)),
            available: option.dataset.available === 'true',
            title: selectedVariant?.title || this.normalizeVariantTitle('', options),
          };
        }

        const options = [
          selector.dataset.option1 || '',
          selector.dataset.option2 || '',
          selector.dataset.option3 || '',
        ];

        return {
          id: this.toInt(selector.value),
          price: this.toInt(selector.dataset.price),
          compareAt: this.toInt(selector.dataset.compareAt, this.toInt(selector.dataset.price)),
          available: selector.dataset.available === 'true',
          title: this.normalizeVariantTitle(selector.dataset.title, options),
        };
      }

      updateAddonItemPrice(item) {
        const selection = this.getAddonSelection(item);
        if (!selection) return;

        const variantTitleEl = item.querySelector('[data-addon-variant-title]');
        const priceEl = item.querySelector('[data-price-current]');
        const compareEl = item.querySelector('[data-price-compare]');
        const comparePrice = Math.max(selection.compareAt || selection.price, selection.price);

        if (variantTitleEl) {
          variantTitleEl.textContent = selection.title || '';
        }

        if (priceEl) {
          priceEl.textContent = this.formatMoney(selection.price);
        }

        if (compareEl) {
          if (comparePrice > selection.price) {
            compareEl.textContent = this.formatMoney(comparePrice);
            compareEl.classList.remove('is-hidden');
          } else {
            compareEl.textContent = '';
            compareEl.classList.add('is-hidden');
          }
        }

        this.updateItemDiscountBadge(item, selection.price, comparePrice);
      }

      updateItemDiscountBadge(item, currentPrice, comparePrice) {
        if (!item) return;

        const badge = item.querySelector('[data-discount-badge]');
        if (!badge) return;

        const price = this.toInt(currentPrice, 0);
        const compare = this.toInt(comparePrice, 0);

        if (!price || !compare || compare <= price) {
          badge.textContent = '';
          badge.classList.add('is-hidden');
          return;
        }

        const discountPercent = Math.round(((compare - price) / compare) * 100);
        if (discountPercent <= 0) {
          badge.textContent = '';
          badge.classList.add('is-hidden');
          return;
        }

        badge.textContent = `-${discountPercent}%`;
        badge.classList.remove('is-hidden');
      }

      getEffectiveDiscountedPrice(discountedPrice, originalPrice) {
        const normalizedOriginal = this.toInt(originalPrice, 0);
        if (discountedPrice === null || discountedPrice === undefined) return normalizedOriginal;

        const normalizedDiscounted = this.toInt(discountedPrice, normalizedOriginal);
        return Math.max(Math.min(normalizedDiscounted, normalizedOriginal), 0);
      }

      updateMainItemDiscountedPrice(discountedPrice) {
        const mainItemEl = this.querySelector('[data-main-item]');
        if (!mainItemEl) return;

        const priceEl = mainItemEl.querySelector('[data-price-current]');
        const compareEl = mainItemEl.querySelector('[data-price-compare]');
        const mainVariant = this.mainVariants.get(String(this.mainVariantId));
        const basePrice = this.mainVariantPrice;
        const originalPrice = Math.max(
          this.toInt(mainVariant?.compare_at_price, basePrice),
          basePrice
        );
        const effectivePrice = this.getEffectiveDiscountedPrice(discountedPrice, basePrice);

        if (priceEl) {
          priceEl.textContent = this.formatMoney(effectivePrice);
        }

        if (compareEl) {
          if (originalPrice > effectivePrice) {
            compareEl.textContent = this.formatMoney(originalPrice);
            compareEl.classList.remove('is-hidden');
          } else {
            compareEl.textContent = '';
            compareEl.classList.add('is-hidden');
          }
        }

        this.updateItemDiscountBadge(mainItemEl, effectivePrice, originalPrice);
      }

      updateAddonItemDiscountedPrice(item, discountedPrice) {
        const selection = this.getAddonSelection(item);
        if (!selection) return;

        const priceEl = item.querySelector('[data-price-current]');
        const compareEl = item.querySelector('[data-price-compare]');
        const basePrice = selection.price;
        const originalPrice = Math.max(this.toInt(selection.compareAt, basePrice), basePrice);
        const effectivePrice = this.getEffectiveDiscountedPrice(discountedPrice, basePrice);

        if (priceEl) {
          priceEl.textContent = this.formatMoney(effectivePrice);
        }

        if (compareEl) {
          if (originalPrice > effectivePrice) {
            compareEl.textContent = this.formatMoney(originalPrice);
            compareEl.classList.remove('is-hidden');
          } else {
            compareEl.textContent = '';
            compareEl.classList.add('is-hidden');
          }
        }

        this.updateItemDiscountBadge(item, effectivePrice, originalPrice);
      }

      resetItemPricesToOriginal() {
        this.updateMainItemDiscountedPrice(this.mainVariantPrice);
        this.addonItems.forEach((item) => this.updateAddonItemDiscountedPrice(item, null));
      }

      extractVariantId(merchandiseId) {
        if (!merchandiseId || typeof merchandiseId !== 'string') return 0;
        const rawId = merchandiseId.split('/').pop();
        return this.toInt(rawId, 0);
      }

      getQuoteUnitPriceMap(quoteResult) {
        return quoteResult?.lineUnitPricesByVariantId || {};
      }

      applyQuoteToItemPrices(quoteResult) {
        const lineUnitPriceMap = this.getQuoteUnitPriceMap(quoteResult);
        const mainLinePrice = lineUnitPriceMap[String(this.mainVariantId)];
        const mainDiscounted = mainLinePrice?.total;

        this.updateMainItemDiscountedPrice(mainDiscounted);

        this.addonItems.forEach((item) => {
          const selection = this.getAddonSelection(item);
          if (!selection) return;

          if (!selection.available) {
            this.updateAddonItemDiscountedPrice(item, null);
            return;
          }

          const linePrice = lineUnitPriceMap[String(selection.id)];
          this.updateAddonItemDiscountedPrice(item, linePrice?.total);
        });
      }

      getItemQuantity(item) {
        const quantity = this.toInt(item.dataset.itemQuantity, 1);
        return quantity > 0 ? quantity : 1;
      }

      collectCartItems() {
        const items = [];

        if (this.mainVariantId) {
          items.push({ id: this.mainVariantId, quantity: this.mainQuantity });
        }

        this.addonItems.forEach((item) => {
          const selection = this.getAddonSelection(item);
          if (!selection || !selection.id || !selection.available) return;

          items.push({ id: selection.id, quantity: this.getItemQuantity(item) });
        });

        return items;
      }

      buildQuoteLines() {
        const quantityByVariantId = new Map();

        this.collectCartItems().forEach((item) => {
          const variantId = this.toInt(item.id);
          const quantity = this.toInt(item.quantity, 0);
          if (!variantId || quantity <= 0) return;

          quantityByVariantId.set(variantId, (quantityByVariantId.get(variantId) || 0) + quantity);
        });

        return Array.from(quantityByVariantId.entries()).map(([variantId, quantity]) => ({
          merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
          quantity,
        }));
      }

      updateTotals() {
        const currentTotal = this.calculateSelectedOriginalTotal();
        const compareTotal = this.calculateSelectedCompareTotal(currentTotal);
        this.resetItemPricesToOriginal();
        this.renderTotals(compareTotal, currentTotal);
        this.syncButtonState();
        this.requestQuote(compareTotal, currentTotal);
      }

      calculateSelectedOriginalTotal() {
        let originalTotal = this.mainVariantPrice * this.mainQuantity;

        this.addonItems.forEach((item) => {
          const selection = this.getAddonSelection(item);
          if (!selection || !selection.available) return;

          originalTotal += selection.price * this.getItemQuantity(item);
        });

        return originalTotal;
      }

      calculateSelectedCompareTotal(currentTotal = 0) {
        const mainVariant = this.mainVariants.get(String(this.mainVariantId));
        const mainCompareUnitPrice = Math.max(
          this.toInt(mainVariant?.compare_at_price, this.mainVariantPrice),
          this.mainVariantPrice
        );
        let compareTotal = mainCompareUnitPrice * this.mainQuantity;

        this.addonItems.forEach((item) => {
          const selection = this.getAddonSelection(item);
          if (!selection || !selection.available) return;

          const compareUnitPrice = Math.max(
            this.toInt(selection.compareAt, selection.price),
            selection.price
          );
          compareTotal += compareUnitPrice * this.getItemQuantity(item);
        });

        return Math.max(compareTotal, currentTotal);
      }

      renderTotals(originalTotal, discountedTotal) {
        const saving = Math.max(originalTotal - discountedTotal, 0);

        if (this.totalDiscountedEl) {
          this.totalDiscountedEl.textContent = this.formatMoney(discountedTotal);
        }

        if (this.totalOriginalEl) {
          if (saving > 0) {
            this.totalOriginalEl.textContent = this.formatMoney(originalTotal);
            this.totalOriginalEl.classList.remove('is-hidden');
          } else {
            this.totalOriginalEl.textContent = '';
            this.totalOriginalEl.classList.add('is-hidden');
          }
        }

        if (this.totalSavingEl) {
          if (saving > 0) {
            this.totalSavingEl.textContent = `You save ${this.formatMoney(saving)}`;
            this.totalSavingEl.classList.remove('is-hidden');
          } else {
            this.totalSavingEl.textContent = '';
            this.totalSavingEl.classList.add('is-hidden');
          }
        }
      }

      syncButtonState() {
        if (!this.addButton) return;

        const items = this.collectCartItems();
        const shouldDisable = !this.mainVariantId || !this.mainVariantAvailable || items.length === 0;

        this.addButton.disabled = shouldDisable;
      }

      setQuoteState(state) {
        this.quoteState = state;
      }

      amountStringToCents(amount, fallback = 0) {
        const parsedAmount = Number.parseFloat(amount);
        if (!Number.isFinite(parsedAmount)) return fallback;
        return Math.round(parsedAmount * 100);
      }

      normalizeQuoteCart(cart) {
        const lineUnitPricesByVariantId = {};
        const lineTotals = (cart?.lines?.nodes || []).reduce(
          (totals, line) => {
            const lineSubtotal = this.amountStringToCents(line?.cost?.subtotalAmount?.amount);
            const lineTotal = this.amountStringToCents(line?.cost?.totalAmount?.amount, lineSubtotal);
            const lineQuantity = Math.max(this.toInt(line?.quantity, 0), 1);
            const variantId = this.extractVariantId(line?.merchandise?.id);

            totals.subtotal += lineSubtotal;
            totals.total += lineTotal;

            if (variantId) {
              lineUnitPricesByVariantId[String(variantId)] = {
                subtotal: Math.round(lineSubtotal / lineQuantity),
                total: Math.round(lineTotal / lineQuantity),
              };
            }

            return totals;
          },
          { subtotal: 0, total: 0 }
        );

        const cartSubtotal = this.amountStringToCents(cart?.cost?.subtotalAmount?.amount);
        const cartTotal = this.amountStringToCents(cart?.cost?.totalAmount?.amount, cartSubtotal);
        const hasLineTotals = lineTotals.subtotal > 0 || lineTotals.total > 0;
        const subtotal = hasLineTotals ? lineTotals.subtotal : cartSubtotal;
        const total = hasLineTotals ? lineTotals.total : cartTotal;

        const allocations = (cart?.discountAllocations || []).map((allocation) => ({
          title: allocation.title || allocation.code || '',
          discountedAmount: this.amountStringToCents(allocation?.discountedAmount?.amount),
        }));

        const lineAllocations = (cart?.lines?.nodes || []).flatMap((line) =>
          (line?.discountAllocations || []).map((allocation) => ({
            title: allocation.title || allocation.code || '',
            discountedAmount: this.amountStringToCents(allocation?.discountedAmount?.amount),
          }))
        );

        return {
          subtotal,
          total,
          savings: Math.max(subtotal - total, 0),
          currency: cart?.cost?.totalAmount?.currencyCode || '',
          allocations: allocations.concat(lineAllocations),
          lineUnitPricesByVariantId,
        };
      }

      async quoteBundle(lines) {
        if (!lines.length) {
          return {
            subtotal: 0,
            total: 0,
            savings: 0,
            currency: '',
            allocations: [],
            lineUnitPricesByVariantId: {},
          };
        }

        const controller = new AbortController();
        this.quoteAbortController = controller;

        const timeoutId = window.setTimeout(() => {
          controller.abort();
        }, 2500);

        try {
          const variables = {
            input: {
              lines,
            },
          };

          if (this.storefrontCountry) {
            variables.country = this.storefrontCountry;
          }

          if (this.storefrontLanguage) {
            variables.language = this.storefrontLanguage;
          }

          const response = await fetch(this.storefrontApiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              ...(this.storefrontAccessToken
                ? { 'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken }
                : {}),
            },
            body: JSON.stringify({
              operationName: 'FbtQuote',
              query: FBT_STOREFRONT_QUOTE_MUTATION,
              variables,
            }),
            signal: controller.signal,
          });

          const payload = await response.json().catch(() => null);
          if (!response.ok || !payload) {
            throw new Error('Failed to fetch bundle quote.');
          }

          if (Array.isArray(payload.errors) && payload.errors.length) {
            throw new Error(payload.errors[0]?.message || 'Failed to fetch bundle quote.');
          }

          const cartCreatePayload = payload?.data?.cartCreate;
          const userError = cartCreatePayload?.userErrors?.[0]?.message;

          if (!cartCreatePayload?.cart || userError) {
            throw new Error(userError || 'Failed to fetch bundle quote.');
          }

          return this.normalizeQuoteCart(cartCreatePayload.cart);
        } finally {
          window.clearTimeout(timeoutId);
        }
      }

      applyQuoteToSummary(quoteResult) {
        this.quotedSubtotal = quoteResult.subtotal;
        this.quotedTotal = quoteResult.total;
        this.quotedSavings = quoteResult.savings;
        this.applyQuoteToItemPrices(quoteResult);
        const compareTotal = this.calculateSelectedCompareTotal(quoteResult.subtotal);
        const summaryOriginalTotal = Math.max(compareTotal, quoteResult.subtotal);
        this.renderTotals(summaryOriginalTotal, quoteResult.total);
      }

      requestQuote(compareTotal, currentTotal) {
        const quoteLines = this.buildQuoteLines();

        if (!quoteLines.length || !this.mainVariantId || !this.mainVariantAvailable) {
          if (this.quoteDebounceTimer) {
            window.clearTimeout(this.quoteDebounceTimer);
            this.quoteDebounceTimer = null;
          }

          if (this.quoteAbortController) {
            this.quoteAbortController.abort();
            this.quoteAbortController = null;
          }

          this.quoteRequestId += 1;
          this.setQuoteState('idle');
          return;
        }

        if (this.quoteDebounceTimer) {
          window.clearTimeout(this.quoteDebounceTimer);
        }

        if (this.quoteAbortController) {
          this.quoteAbortController.abort();
          this.quoteAbortController = null;
        }

        const requestId = ++this.quoteRequestId;
        this.setQuoteState('loading');

        this.quoteDebounceTimer = window.setTimeout(async () => {
          this.quoteDebounceTimer = null;

          try {
            const quoteResult = await this.quoteBundle(quoteLines);
            if (requestId !== this.quoteRequestId) return;

            this.applyQuoteToSummary(quoteResult);
            this.setQuoteState('success');
          } catch (error) {
            if (requestId !== this.quoteRequestId) return;

            this.resetItemPricesToOriginal();
            this.renderTotals(compareTotal, currentTotal);
            this.setQuoteState('error');
          } finally {
            if (requestId === this.quoteRequestId) {
              this.quoteAbortController = null;
            }
          }
        }, 250);
      }

      async handleAddToCart() {
        if (!this.addButton || this.addButton.disabled || this.addButton.classList.contains('loading')) return;

        const items = this.collectCartItems();
        if (!items.length) return;

        this.addButton.classList.add('loading');
        this.addButton.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to add bundle.');
          }

          window.location.href = window.routes?.cart_url || '/cart';
        } catch (error) {
          console.error('[FBT] add-to-cart failed', error);
        } finally {
          this.addButton.classList.remove('loading');
          this.addButton.removeAttribute('aria-busy');
          this.syncButtonState();
        }
      }

      formatMoney(cents) {
        const value = Number(cents || 0);

        const formatWithDelimiters = (amount, precision, thousands, decimal) => {
          const number = (amount / 100.0).toFixed(precision);
          const parts = number.split('.');
          const dollarsAmount = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
          const centsAmount = parts[1] ? `${decimal}${parts[1]}` : '';

          return `${dollarsAmount}${centsAmount}`;
        };

        const replacements = {
          '{{amount}}': formatWithDelimiters(value, 2, ',', '.'),
          '{{amount_no_decimals}}': formatWithDelimiters(value, 0, ',', '.'),
          '{{amount_with_comma_separator}}': formatWithDelimiters(value, 2, '.', ','),
          '{{amount_no_decimals_with_comma_separator}}': formatWithDelimiters(value, 0, '.', ','),
          '{{amount_with_apostrophe_separator}}': formatWithDelimiters(value, 2, "'", '.'),
        };

        let output = this.moneyFormat;
        Object.keys(replacements).forEach((key) => {
          if (output.includes(key)) output = output.replace(key, replacements[key]);
        });

        return output;
      }

      toInt(value, fallback = 0) {
        const parsedValue = parseInt(value, 10);
        return Number.isFinite(parsedValue) ? parsedValue : fallback;
      }
    }
  );
}
